import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  ReactNode
} from 'react'
import { notification } from 'antd'
import { v4 as uuidv4 } from 'uuid'

export interface PrintPayload {
  documentType: string
  printerRole: string
  copies: number
  orderId: string
  restaurant: string
  gstin?: string
  address?: string
  token?: string
  serviceType?: string
  comments?: string
  customerName?: string
  tableNo?: string
  subtotal: number
  tax: number
  discount: number
  total: number
  payMethod?: string
  date?: string
  time?: string
  empNo?: string
  orderTotalWithoutDiscount?: number
  discountAmount?: number
  cgst?: number
  sgst?: number
  packagingCharges?: number
  packagingGstTotal?: number
  items: Array<{
    qty: number
    name: string
    price: number
    notes: string
    originalBasePrice?: number
    discountAmount?: number
  }>
}

export interface ConfigPayload {
  role: string
  name: string
  connectionType: string
  baudRate: number
}

interface PrintAgentContextType {
  isConnected: boolean
  connectionTarget: string
  reconnect: () => void
  sendPrintJob: (payload: PrintPayload) => void
  sendConfig: (payload: ConfigPayload) => void
}

const PrintAgentContext = createContext<PrintAgentContextType | undefined>(
  undefined
)

export const usePrintAgent = () => {
  const context = useContext(PrintAgentContext)
  if (!context) {
    throw new Error('usePrintAgent must be used within a PrintAgentProvider')
  }
  return context
}

interface ProviderProps {
  children: ReactNode
}

const DISCOVERY_ENDPOINTS = [
  'http://127.0.0.1:5959/discovery',
  'http://localhost:5959/discovery',
  'http://127.0.0.1:5001/discovery',
  'http://localhost:5001/discovery'
]

const LEGACY_WS_ENDPOINTS = [
  'ws://127.0.0.1:5001/ws',
  'ws://localhost:5001/ws',
  'ws://127.0.0.1:8080/ws',
  'ws://localhost:8080/ws',
  'ws://127.0.0.1:8080',
  'ws://localhost:8080'
]

const DISCOVERY_TIMEOUT_MS = 1500
const OVERRIDE_KEY = 'print_agent_ws_override'

interface DiscoveryResponse {
  wsUrl?: string
  configPort?: number
}

export const PrintAgentProvider: React.FC<ProviderProps> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false)
  const [connectionTarget, setConnectionTarget] = useState(
    LEGACY_WS_ENDPOINTS[0]
  )
  const wsRef = useRef<WebSocket | null>(null)
  const retryCount = useRef(0)
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isConnectingRef = useRef(false)
  const endpointIndexRef = useRef(0)
  const endpointCandidatesRef = useRef<string[]>(LEGACY_WS_ENDPOINTS)

  const normalizeWs = (value: string): string | null => {
    if (!value) return null
    const trimmed = value.trim()
    if (!trimmed.startsWith('ws://') && !trimmed.startsWith('wss://')) {
      return null
    }
    return trimmed
  }

  const uniqueEndpoints = (values: string[]): string[] => {
    const seen = new Set<string>()
    return values.filter((value) => {
      if (!value || seen.has(value)) return false
      seen.add(value)
      return true
    })
  }

  const fetchDiscovery = useCallback(async (): Promise<string[]> => {
    for (const endpoint of DISCOVERY_ENDPOINTS) {
      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), DISCOVERY_TIMEOUT_MS)
      try {
        const response = await fetch(endpoint, {
          method: 'GET',
          signal: controller.signal
        })
        if (!response.ok) {
          continue
        }
        const data = (await response.json()) as DiscoveryResponse
        const discovered: string[] = []

        if (typeof data.wsUrl === 'string') {
          const parsed = normalizeWs(data.wsUrl)
          if (parsed) {
            discovered.push(parsed)
          }
        }

        if (typeof data.configPort === 'number' && data.configPort > 0) {
          discovered.push(`ws://127.0.0.1:${data.configPort}/ws`)
          discovered.push(`ws://localhost:${data.configPort}/ws`)
        }

        if (discovered.length > 0) {
          return uniqueEndpoints(discovered)
        }
      } catch {
        // Try next discovery endpoint.
      } finally {
        clearTimeout(timeout)
      }
    }

    return []
  }, [])

  const resolveEndpoints = useCallback(async (): Promise<string[]> => {
    const discoveredEndpoints = await fetchDiscovery()
    const overrideRaw = window.localStorage.getItem(OVERRIDE_KEY) || ''
    const overrideEndpoint = normalizeWs(overrideRaw)

    const merged = uniqueEndpoints([
      ...discoveredEndpoints,
      ...(overrideEndpoint ? [overrideEndpoint] : []),
      ...LEGACY_WS_ENDPOINTS
    ])

    endpointCandidatesRef.current =
      merged.length > 0 ? merged : LEGACY_WS_ENDPOINTS
    return endpointCandidatesRef.current
  }, [fetchDiscovery])

  const connect = useCallback(
    (preferredIndex?: number) => {
      if (
        wsRef.current?.readyState === WebSocket.OPEN ||
        wsRef.current?.readyState === WebSocket.CONNECTING ||
        isConnectingRef.current
      ) {
        return
      }

      if (typeof preferredIndex === 'number') {
        endpointIndexRef.current = preferredIndex
      }
      isConnectingRef.current = true

      void resolveEndpoints().then((candidates) => {
        const safeCandidates =
          candidates.length > 0 ? candidates : LEGACY_WS_ENDPOINTS
        if (endpointIndexRef.current >= safeCandidates.length) {
          endpointIndexRef.current = 0
        }
        const endpoint =
          safeCandidates[endpointIndexRef.current] || safeCandidates[0]
        setConnectionTarget(endpoint)

        const ws = new WebSocket(endpoint)

        ws.onopen = () => {
          console.log('[Print Agent] Connected', endpoint)
          setIsConnected(true)
          isConnectingRef.current = false
          retryCount.current = 0
          if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current)
            retryTimeoutRef.current = null
          }
        }

        ws.onclose = () => {
          console.log('[Print Agent] Disconnected')
          setIsConnected(false)
          isConnectingRef.current = false
          wsRef.current = null

          // Exponential backoff reconnect
          const delay = Math.min(1000 * Math.pow(2, retryCount.current), 30000)
          const list = endpointCandidatesRef.current
          const nextEndpointIndex =
            list.length > 0 ? (endpointIndexRef.current + 1) % list.length : 0
          endpointIndexRef.current = nextEndpointIndex
          console.log(
            `[Print Agent] Reconnecting in ${delay / 1000} seconds using ${list[nextEndpointIndex]}...`
          )
          retryTimeoutRef.current = setTimeout(() => {
            retryCount.current += 1
            connect()
          }, delay)
        }

        ws.onerror = (error) => {
          console.error('[Print Agent] WebSocket error', error)
          isConnectingRef.current = false
          ws.close()
        }

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data)
            console.log('[Print Agent] Message received:', data)

            if (data.event === 'PRINT_FAILED') {
              notification.error({
                message: 'Print Failed',
                description:
                  data.message || `Error Code: ${data.errorCode || 'UNKNOWN'}`,
                placement: 'bottomRight'
              })
            } else if (data.event === 'PRINT_SUCCESS') {
              notification.success({
                message: 'Print Successful',
                description: `Receipt for order printed successfully.`,
                placement: 'bottomRight'
              })
            } else if (data.event === 'CONFIG_ACK') {
              notification.success({
                message: 'Printer Configured',
                description:
                  'Configuration saved successfully to the Print Agent.',
                placement: 'bottomRight'
              })
            }
          } catch (e) {
            console.error('[Print Agent] Failed to parse message', e)
          }
        }

        wsRef.current = ws
      })
    },
    [resolveEndpoints]
  )

  const reconnect = useCallback(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current)
      retryTimeoutRef.current = null
    }
    isConnectingRef.current = false
    setIsConnected(false)
    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }
    connect(0)
  }, [connect])

  useEffect(() => {
    connect()

    const reconnectSoon = () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current)
        retryTimeoutRef.current = null
      }
      connect(0)
    }

    const handleOnline = () => reconnectSoon()
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        reconnectSoon()
      }
    }

    window.addEventListener('online', handleOnline)
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      window.removeEventListener('online', handleOnline)
      document.removeEventListener('visibilitychange', handleVisibility)
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current)
      }
      if (wsRef.current) {
        wsRef.current.close()
      }
    }
  }, [connect])

  const sendPrintJob = useCallback((payload: PrintPayload) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const message = {
        version: 1,
        action: 'PRINT',
        requestId: uuidv4(),
        timestamp: new Date().toISOString(),
        payload
      }
      wsRef.current.send(JSON.stringify(message))
    } else {
      notification.warning({
        message: 'Print Agent Offline',
        description:
          'Ensure the Windows Print Agent is running and local discovery is reachable.',
        placement: 'bottomRight'
      })
    }
  }, [])

  const sendConfig = useCallback((configPayload: ConfigPayload) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const message = {
        version: 1,
        action: 'CONFIG',
        requestId: uuidv4(),
        timestamp: new Date().toISOString(),
        configPayload
      }
      wsRef.current.send(JSON.stringify(message))
    } else {
      notification.warning({
        message: 'Print Agent Offline',
        description:
          'Cannot save settings. Ensure the Windows Print Agent is running and connected.',
        placement: 'bottomRight'
      })
    }
  }, [])

  return (
    <PrintAgentContext.Provider
      value={{
        isConnected,
        connectionTarget,
        reconnect,
        sendPrintJob,
        sendConfig
      }}
    >
      {children}
    </PrintAgentContext.Provider>
  )
}
